import type { FamilyMember } from '@/components/order/OrderTypes'
import type { CanonicalParentInfo, Comparator } from '@/ordering/canonicalOrder'
import type { IncidentalFindingsSelection } from '@/types/RequisitionFormTypes'

/** Shown on proband IF when opt-out auto-changed comparators that had opted in to incidental findings. */
export const COMPARATOR_INCIDENTAL_MODIFIED_NOTICE =
  'Comparator opt-in for incidental findings requires proband opt-in. Comparators who had been opted in have automatically been opted out.'

/** Shown on a comparator's IF section when the proband opted out, locking it to opt-out. */
export const COMPARATOR_INCIDENTAL_LOCKED_NOTICE =
  'Incidental findings opt-in is unavailable because the patient opted out. Comparators can only opt in when the patient opts in.'

/** Comparator incidental findings cannot opt in when the proband opted out. */
export function probandIncidentalFindingsLocksComparator(
  probandSelection: IncidentalFindingsSelection | null | undefined
): boolean {
  return probandSelection === 'opt-out'
}

export function comparatorHadIncidentalOptIn(selection: IncidentalFindingsSelection | null | undefined): boolean {
  return selection === 'opt-in'
}

export function familyMemberIncidentalLockedToOptOut(member: FamilyMember): FamilyMember {
  if (member.incidentalFindingsSelection === 'opt-out') {
    return member
  }
  return { ...member, incidentalFindingsSelection: 'opt-out' }
}

/** Comparator modal Discard: revert to open baseline but keep IF opt-out when proband opted out. */
export function familyMemberForComparatorDiscard(
  baseline: FamilyMember,
  probandSelection: IncidentalFindingsSelection | null | undefined
): FamilyMember {
  return probandIncidentalFindingsLocksComparator(probandSelection)
    ? familyMemberIncidentalLockedToOptOut(baseline)
    : baseline
}

export type ParentIncidentalProbandOptOutPatch = {
  patch: Pick<CanonicalParentInfo, 'includeIncidentalFindings'>
  modifiedFromOptIn: boolean
}

/** Canonical patch when proband opt-out requires comparator IF = false. */
export function parentIncidentalPatchWhenProbandOptOut(
  parent: CanonicalParentInfo | null | undefined,
  probandSelection: IncidentalFindingsSelection | null | undefined
): ParentIncidentalProbandOptOutPatch | null {
  if (!probandIncidentalFindingsLocksComparator(probandSelection) || !parent) {
    return null
  }
  if (parent.includeIncidentalFindings === false) {
    return null
  }
  return {
    patch: { includeIncidentalFindings: false },
    modifiedFromOptIn: parent.includeIncidentalFindings === true,
  }
}

/**
 * Comparator-row equivalent of {@link parentIncidentalPatchWhenProbandOptOut}.
 *
 * GEN-3484: when the proband opts out of incidental findings, every comparator
 * is locked to opt-out. The interactive UI reflects this visually, but on
 * reload the stored comparator `secondaryConsent.includeIncidentalFindings`
 * can still be `null` (never an explicit provider choice), which the send
 * completeness gate reads as "unanswered" and blocks updates. This returns the
 * `includeIncidentalFindings: false` patch needed to reconcile the stored value
 * with the displayed lock; returns `null` when no change is needed (proband not
 * opted out, or the comparator is already an explicit opt-out).
 */
export function comparatorIncidentalPatchWhenProbandOptOut(
  comparator: Comparator | null | undefined,
  probandSelection: IncidentalFindingsSelection | null | undefined
): Pick<Comparator, 'secondaryConsent'> | null {
  if (!probandIncidentalFindingsLocksComparator(probandSelection) || !comparator) {
    return null
  }
  if (comparator.secondaryConsent?.includeIncidentalFindings === false) {
    return null
  }
  return {
    secondaryConsent: {
      ...(comparator.secondaryConsent ?? {}),
      includeIncidentalFindings: false,
    },
  }
}
